import React, { useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from '../../services/api';
import authService from '../../services/authService';
import {
  DeliveryNeighborhoodReport,
  DeliveryNeighborhoodReportItem,
  DeliveryNeighborhoodReportDetail,
} from '../../types/reports';
import { StatusPedido } from '../../types/pedido';
import logo from '../../assets/logo.jpeg';

const STATUS_OPTIONS: { value: StatusPedido; label: string }[] = [
  { value: StatusPedido.RECEBIDO, label: 'Recebido' },
  { value: StatusPedido.EM_PREPARO, label: 'Em preparo' },
  { value: StatusPedido.PRONTO, label: 'Pronto' },
  { value: StatusPedido.EM_ROTA, label: 'Em rota' },
  { value: StatusPedido.ENTREGUE, label: 'Entregue' },
  { value: StatusPedido.CANCELADO, label: 'Cancelado' },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDateDisplay = (value?: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const printStyles = `
  @page {
    size: A4 portrait;
    margin: 18mm 16mm 20mm 16mm;
  }

  body {
    background: #ffffff !important;
    color: #0f172a !important;
    font-family: 'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
  }

  .print-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    animation: fadeIn 0.4s ease-in;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 18px;
    border-bottom: 2px solid #d9e2ec;
    gap: 18px;
  }

  .print-brand {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .print-brand img {
    width: 84px;
    height: 84px;
    border-radius: 20px;
    object-fit: cover;
    border: 1px solid #d9e2ec;
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
  }

  .print-brand h1 {
    margin: 0;
    font-size: 22px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 700;
    color: #0f172a;
  }

  .print-brand p {
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #475569;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .print-meta {
    text-align: right;
    font-size: 12px;
    line-height: 1.5;
    color: #334155;
  }

  .print-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
  }

  .print-summary-card {
    background: linear-gradient(145deg, #f8fbff, #eef2ff);
    border-radius: 16px;
    padding: 16px 20px;
    border: 1px solid rgba(99, 102, 241, 0.18);
    box-shadow: 0 10px 18px rgba(79, 70, 229, 0.06);
  }

  .print-summary-card .label {
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #6366f1;
    margin-bottom: 6px;
  }

  .print-summary-card .value {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
  }

  .print-section-title {
    margin: 28px 0 12px;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #0f172a;
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .print-table thead th {
    background: #0f172a;
    color: #ffffff;
    padding: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .print-table tbody td {
    border: 1px solid #d9e2ec;
    padding: 10px 12px;
  }

  .print-table tbody tr:nth-of-type(odd) {
    background: #f8fafc;
  }

  .print-footer {
    margin-top: 48px;
    display: flex;
    gap: 96px;
  }

  .print-footer .sign-block {
    flex: 1;
    border-top: 1.4px solid #0f172a;
    padding-top: 18px;
    font-size: 12px;
    text-align: center;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #475569;
  }

  @media print {
    .print-hide {
      display: none !important;
    }
  }
`;

interface ParsedFilters {
  dataInicio?: string;
  dataFim?: string;
  status: StatusPedido[];
  telefone?: string;
  cnpj?: string;
  courierId?: string;
  courierName?: string;
  viewMode?: 'agregado' | 'analitico';
}

const parseFiltersFromSearch = (search: string): ParsedFilters => {
  const params = new URLSearchParams(search);
  const dataInicio = params.get('dataInicio') || undefined;
  const dataFim = params.get('dataFim') || undefined;
  const statusParams = params.getAll('status');
  const telefone = params.get('telefone') || undefined;
  const cnpj = params.get('cnpj') || undefined;
  const courierId = params.get('courierId') || undefined;
  const courierName = params.get('courierName') || undefined;
  const viewModeParam = params.get('viewMode');

  const status = statusParams
    .map((value) => value as StatusPedido)
    .filter((value) => Object.values(StatusPedido).includes(value));

  return {
    dataInicio,
    dataFim,
    status: status.length > 0 ? status : Object.values(StatusPedido),
    telefone,
    cnpj,
    courierId,
    courierName,
    viewMode: viewModeParam === 'analitico' ? 'analitico' : 'agregado',
  };
};

const buildQueryFilters = (filters: ParsedFilters) => {
  const query: {
    dataInicio?: string;
    dataFim?: string;
    status?: string[];
    telefone?: string;
    cnpj?: string;
    courierId?: string;
  } = {};

  if (filters.dataInicio) {
    query.dataInicio = new Date(`${filters.dataInicio}T00:00:00`).toISOString();
  }

  if (filters.dataFim) {
    query.dataFim = new Date(`${filters.dataFim}T23:59:59`).toISOString();
  }

  if (filters.status.length > 0) {
    query.status = filters.status;
  }

  if (filters.telefone) {
    query.telefone = filters.telefone.replace(/\D/g, '');
  }

  if (filters.cnpj) {
    query.cnpj = filters.cnpj.replace(/\D/g, '');
  }

  if (filters.courierId) {
    query.courierId = filters.courierId;
  }

  return query;
};

interface PrintLayoutProps {
  totalizadores: {
    totalPedidos: number;
    totalTaxaEntrega: number;
    mediaTaxa: number;
  };
  agregados: DeliveryNeighborhoodReportItem[];
  detalhes: DeliveryNeighborhoodReportDetail[];
  filters: ParsedFilters;
  statusLabels: string;
  viewMode: 'agregado' | 'analitico';
}

const PrintLayout: React.FC<PrintLayoutProps> = ({
  totalizadores,
  agregados,
  detalhes,
  filters,
  statusLabels,
  viewMode,
}) => {
  const user = authService.getUser();

  return (
    <div className="print-wrapper">
      <div className="print-header">
        <div className="print-brand">
          <img src={logo} alt="Logotipo" />
          <div>
            <h1>Better Delivery</h1>
            <p>Relatório de entregas por bairro</p>
          </div>
        </div>
        <div className="print-meta">
          <div>Emitido em: {formatDateTime(new Date().toISOString())}</div>
          <div>
            Período: {formatDateDisplay(filters.dataInicio)} – {formatDateDisplay(filters.dataFim)}
          </div>
          <div>Status considerados: {statusLabels || 'Todos'}</div>
          {filters.courierName && <div>Entregador: {filters.courierName}</div>}
          {user?.name && <div>Responsável: {user.name}</div>}
          {user?.cnpj && <div>CNPJ: {user.cnpj}</div>}
        </div>
      </div>

      <div className="print-summary">
        <div className="print-summary-card">
          <div className="label">Total de entregas</div>
          <div className="value">{totalizadores.totalPedidos}</div>
        </div>
        <div className="print-summary-card">
          <div className="label">Total em taxas</div>
          <div className="value">{formatCurrency(totalizadores.totalTaxaEntrega)}</div>
        </div>
        <div className="print-summary-card">
          <div className="label">Taxa média</div>
          <div className="value">{formatCurrency(totalizadores.mediaTaxa)}</div>
        </div>
      </div>

      {viewMode === 'agregado' && (
        <div>
          <div className="print-section-title">Resumo por bairro</div>
          {agregados.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: 12 }}>Nenhum dado encontrado para o período selecionado.</p>
          ) : (
            <table className="print-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Bairro</th>
                  <th style={{ textAlign: 'right' }}>Qtd. de entregas</th>
                  <th style={{ textAlign: 'right' }}>Taxa do bairro</th>
                  <th style={{ textAlign: 'right' }}>Total de taxas</th>
                </tr>
              </thead>
              <tbody>
                {agregados.map((item) => (
                  <tr key={`print-agg-${item.bairro}`}>
                    <td>{item.bairro}</td>
                    <td style={{ textAlign: 'right' }}>{item.quantidadePedidos}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.taxaDoBairro)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.valorTotalTaxas)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {viewMode === 'analitico' && (
        <div>
          <div className="print-section-title">Entregas detalhadas</div>
          {detalhes.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: 12 }}>Nenhuma entrega detalhada para este período.</p>
          ) : (
            <table className="print-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Data</th>
                  <th style={{ textAlign: 'left' }}>Bairro</th>
                  <th style={{ textAlign: 'left' }}>Cliente</th>
                  <th style={{ textAlign: 'right' }}>Taxa</th>
                  <th style={{ textAlign: 'left' }}>Status</th>
                  <th style={{ textAlign: 'left' }}>Entregador</th>
                </tr>
              </thead>
              <tbody>
                {detalhes.map((item) => (
                  <tr key={`print-det-${item.id}`}>
                    <td>{formatDateTime(item.dataPedido)}</td>
                    <td>{item.bairro}</td>
                    <td>{item.cliente}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.taxaEntrega)}</td>
                    <td>{item.status}</td>
                    <td>{item.courierName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="print-footer">
        <div className="sign-block">Conferido por</div>
        <div className="sign-block">Assinatura do entregador</div>
      </div>
    </div>
  );
};

export const DeliveryNeighborhoodReportPrintPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parseFiltersFromSearch(location.search), [location.search]);
  const queryFilters = useMemo(() => buildQueryFilters(filters), [filters]);

  const { data, isLoading, isError } = useQuery<DeliveryNeighborhoodReport>({
    queryKey: ['deliveries-report-print', queryFilters],
    queryFn: () => reportsService.getDeliveriesByNeighborhood(queryFilters),
    enabled: filters.status.length > 0,
  });

  const totalizadores = useMemo(() => {
    const totalPedidos = data?.totalPedidos ?? 0;
    const totalTaxaEntrega = data?.totalTaxaEntrega ?? 0;
    const mediaTaxa = totalPedidos > 0 ? totalTaxaEntrega / totalPedidos : 0;

    return {
      totalPedidos,
      totalTaxaEntrega,
      mediaTaxa,
    };
  }, [data]);

  const statusLabels = useMemo(() => {
    return filters.status
      .map((status) => STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status)
      .join(', ');
  }, [filters.status]);

  const agregados = data?.agregados ?? [];
  const detalhes = data?.detalhes ?? [];
  const viewMode = filters.viewMode ?? 'agregado';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f6fb',
        py: 4,
        px: { xs: 2, md: 6 },
      }}
    >
      <style>{printStyles}</style>

      <Box
        className="print-hide"
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Visualização para impressão
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revise e clique em "Imprimir" para gerar o PDF. Você pode voltar para ajustar filtros a qualquer momento.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            color="primary"
            onClick={() => window.print()}
            disabled={isLoading}
          >
            Imprimir
          </Button>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" className="print-hide" sx={{ mb: 3 }}>
          Ocorreu um erro ao carregar os dados para impressão. Volte e tente novamente.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: '#ffffff',
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.12)',
          }}
        >
          <PrintLayout
            totalizadores={totalizadores}
            agregados={agregados}
            detalhes={detalhes}
            filters={filters}
            statusLabels={statusLabels}
            viewMode={viewMode}
          />
        </Box>
      )}
    </Box>
  );
};

export default DeliveryNeighborhoodReportPrintPage;
